import React, { useState, useEffect, useRef, useMemo } from "react"
import Icon from "../AppIcon"
import { subscribeAllMyMentees, subscribeMentorshipSessions } from "../../utils/mentorshipHelpers"

// Average days between consecutive logged sessions. Needs at least 2
// sessions to say anything — a single session has no cadence yet.
function computeCadenceDays(sessions) {
  const times = (sessions || [])
    .map((s) => s?.completedAtMs)
    .filter(Boolean)
    .sort((a, b) => a - b)
  if (times.length < 2) return null
  const diffs = []
  for (let i = 1; i < times.length; i++) {
    diffs.push((times[i] - times[i - 1]) / 86400000)
  }
  const avg = diffs.reduce((sum, d) => sum + d, 0) / diffs.length
  return avg
}

function formatCadence(days) {
  if (days == null) return "Not enough data yet"
  if (days < 1) return "Same-day sessions"
  return `Every ${Math.round(days)} day${Math.round(days) === 1 ? "" : "s"}`
}

// variant: "summary" — compact 3-stat row (for the always-visible top card)
//          "full"    — summary row + per-mentee breakdown table
const MentorAnalytics = ({ mentorUid, variant = "summary" }) => {
  const [mentees, setMentees] = useState([])
  const [sessionsByMentorship, setSessionsByMentorship] = useState({})
  const unsubsRef = useRef({})

  useEffect(() => {
    if (!mentorUid) return
    return subscribeAllMyMentees(mentorUid, setMentees)
  }, [mentorUid])

  // Keep one sessions listener per active mentorship, adding/removing as
  // the mentee list changes rather than re-subscribing everything.
  useEffect(() => {
    const currentIds = new Set(mentees.map((m) => m.id))

    mentees.forEach((m) => {
      if (unsubsRef.current[m.id]) return
      unsubsRef.current[m.id] = subscribeMentorshipSessions(m.orgId, m.id, (sessions) => {
        setSessionsByMentorship((prev) => ({ ...prev, [m.id]: sessions }))
      })
    })

    Object.keys(unsubsRef.current).forEach((id) => {
      if (!currentIds.has(id)) {
        unsubsRef.current[id]?.()
        delete unsubsRef.current[id]
        setSessionsByMentorship((prev) => {
          const next = { ...prev }
          delete next[id]
          return next
        })
      }
    })
  }, [mentees])

  useEffect(() => () => {
    Object.values(unsubsRef.current).forEach((u) => u?.())
    unsubsRef.current = {}
  }, [])

  const { menteeCount, totalSessions, overallCadenceLabel, perMentee } = useMemo(() => {
    let total = 0
    const allDiffDays = []
    const rows = mentees.map((m) => {
      const sessions = sessionsByMentorship[m.id] || []
      total += sessions.length
      const times = sessions.map((s) => s?.completedAtMs).filter(Boolean).sort((a, b) => a - b)
      for (let i = 1; i < times.length; i++) allDiffDays.push((times[i] - times[i - 1]) / 86400000)
      const lastSessionMs = times.length > 0 ? times[times.length - 1] : null
      return {
        id: m.id,
        name: m.menteeName || "Mentee",
        sessionCount: sessions.length,
        cadence: computeCadenceDays(sessions),
        lastSessionMs,
      }
    })
    const overallAvg = allDiffDays.length > 0 ? allDiffDays.reduce((a, b) => a + b, 0) / allDiffDays.length : null
    return {
      menteeCount: mentees.length,
      totalSessions: total,
      overallCadenceLabel: formatCadence(overallAvg),
      perMentee: rows,
    }
  }, [mentees, sessionsByMentorship])

  const summaryRow = (
    <div className="grid grid-cols-3 gap-3">
      <div className="bg-card border border-border rounded-xl p-4 text-center">
        <p className="text-2xl font-bold text-gray-900">{menteeCount}</p>
        <p className="text-xs text-muted-foreground mt-1">Mentees Mentored</p>
      </div>
      <div className="bg-card border border-border rounded-xl p-4 text-center">
        <p className="text-2xl font-bold text-gray-900">{totalSessions}</p>
        <p className="text-xs text-muted-foreground mt-1">Sessions Logged</p>
      </div>
      <div className="bg-card border border-border rounded-xl p-4 text-center">
        <p className="text-sm font-semibold text-gray-900 mt-1">{overallCadenceLabel}</p>
        <p className="text-xs text-muted-foreground mt-1">Avg. Session Cadence</p>
      </div>
    </div>
  )

  if (variant === "summary") return summaryRow

  return (
    <div className="space-y-4">
      {summaryRow}

      <div className="bg-card border border-border rounded-xl p-5 space-y-2">
        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">By Mentee</h3>
        {perMentee.length === 0 && (
          <p className="text-sm text-muted-foreground">No mentees yet.</p>
        )}
        {perMentee.map((row) => (
          <div key={row.id} className="flex items-center justify-between py-3 border-b border-border last:border-0 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Icon name="User" size={16} className="text-accent flex-shrink-0" />
              <p className="text-sm font-medium text-gray-900 truncate">{row.name}</p>
            </div>
            <div className="flex items-center gap-6 flex-shrink-0 text-right">
              <div>
                <p className="text-sm font-semibold text-gray-900">{row.sessionCount}</p>
                <p className="text-[10px] text-muted-foreground">sessions</p>
              </div>
              <div className="w-32">
                <p className="text-xs text-gray-900">{formatCadence(row.cadence)}</p>
                <p className="text-[10px] text-muted-foreground">
                  {row.lastSessionMs ? `Last: ${new Date(row.lastSessionMs).toLocaleDateString()}` : "No sessions yet"}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default MentorAnalytics